/**
 * Structured-ingest API — upload a tabular file, review the model's mapping
 * proposal, project the confirmed recipe into the case graph.
 *
 * The proposal is prepared in the background: `analyze` returns a *pending*
 * analysis and the screen polls `get` until it is `ready` (or `failed`).
 * Wire shape is camelCase at the top level; the analysis payloads (facts,
 * column profiles, proposal, preview) keep the library's snake_case keys.
 */

import { apiClient } from './client'

export type AnalysisStatus = 'pending' | 'ready' | 'failed'

export interface TableFacts {
  name: string
  rows: number
  columns: string[]
  header_row: number | null
  synthetic_header: boolean
  residue_above: string[]
  residue_below: string[]
  residue_above_count: number
  residue_below_count: number
  empty_rows_dropped: number
  delimiter: string | null
  decimal: string
  notes: string[]
}

export interface LaneDecision {
  lane: 'document_allowed' | 'structured_forced' | 'document_fallback'
  reason: string
  rows_per_chunk: number
}

export interface ColumnProfile {
  name: string
  kind: string
  n_distinct: number
  null_rate: number
  /** Rows with a value — a tag present in 3 of 240 rows says "3". */
  filled_rows?: number
  is_constant: boolean
  samples: string[]
  recognizers: Record<string, number>
}

export interface SchemaIdentity {
  /** This mapping may keep entities separate while retaining the proposed key as data. */
  merge_enabled?: boolean
  /** Server-validated unique keys, including proposed additions awaiting approval. */
  identity_properties?: string[]
  /** Server validation of the selected key and the evidence used to populate it. */
  identity_error?: string | null
}

export interface SubjectEntity extends SchemaIdentity {
  name: string
  schema_type: string
  unique_property?: string | null
  identity_value?: string | null
  identity_rule?: string | null
  display_value?: string | null
  note?: string | null
  enabled?: boolean
  /** false when the label is not defined by the case schema (the projection would introduce it). */
  schema_match?: boolean
  /** The investigator's explicit yes to a type outside the case schema. */
  accept_new_type?: boolean
  schema_additions?: string[]
}

export interface EntityDescriptor extends SchemaIdentity {
  name: string
  schema_type: string
  unique_property?: string | null
  identity_columns: string[]
  identity_rule?: string | null
  identity_scope?: string | null
  display_columns?: string[]
  facet_columns?: string[]
  filters?: Array<{ column: string; keep: string }>
  note?: string | null
  enabled?: boolean
  schema_match?: boolean
  accept_new_type?: boolean
  schema_additions?: string[]
  /** Actual graph-property destinations, derived by the server from the case schema. */
  property_mappings?: ColumnPropertyMapping[]
}

export interface ColumnPropertyMapping {
  columns: string[]
  property: string
}

export interface RelationshipRule {
  name: string
  kind?: 'structural' | 'event_aggregate'
  from: string
  to: string
  type: string
  direction_rule?: string | null
  grain_default?: string[]
  grain?: string[]
  measures?: string[]
  filters?: Array<{ column: string; keep: string }>
  note?: string | null
  enabled?: boolean
  /** false when no schema pattern `<from label> TYPE <to label>` exists. */
  schema_match?: boolean
  accept_new_type?: boolean
  schema_additions?: string[]
  /** Direct graph-property destinations, normalized by the server for this relationship. */
  property_mappings?: ColumnPropertyMapping[]
}

/** The one extra grain a relationship groups by besides the pair ('pair' when none). */
export function selectedGrain(rel: RelationshipRule): string {
  if (rel.kind === 'structural') return 'pair'
  const extra = (rel.grain ?? rel.grain_default ?? []).find(g => g !== 'pair')
  return extra ?? 'pair'
}

export function withGrain(rel: RelationshipRule, grain: string): RelationshipRule {
  return { ...rel, grain: grain === 'pair' ? ['pair'] : ['pair', grain] }
}

/**
 * The grain the projection can actually apply: the selected one when the preview could
 * count it, else the next listed grain it could (a `direction` grain needs an amount column
 * to read the sign from — without one the executor falls back the same way), else the pair.
 */
export function effectiveGrain(rel: RelationshipRule, preview: RelationshipPreview | null): string {
  const wanted = selectedGrain(rel)
  if (!preview || wanted in preview.grains) return wanted
  const listed = (rel.grain ?? rel.grain_default ?? []).filter(g => g !== 'pair')
  return listed.find(g => g in preview.grains) ?? 'pair'
}

export interface OutputNode {
  name: string
  label: string
  source: 'subject' | 'row'
  count: number | null
  enabled: boolean
  outsideSchema: boolean
}

export interface OutputEdge {
  name: string
  type: string
  kind: 'structural' | 'event_aggregate'
  from: string
  to: string
  grain: string
  count: number | null
  rowsInScope: number | null
  measures: string[]
  enabled: boolean
  outsideSchema: boolean
}

export interface OutputSummary {
  nodes: OutputNode[]
  edges: OutputEdge[]
  /** Totals over enabled items only; null when a count is not known before projection. */
  totals: { nodes: number | null; edges: number | null; labels: number; types: number }
  windows: boolean
}

function bareType(type: string): string {
  const t = type.replace(/^new:/, '').trim()
  const parts = t.split(/\s+/)
  return (parts.length === 3 ? parts[1] : t).replace(/[^A-Za-z0-9_]+/g, '_').toUpperCase()
}

/**
 * What the graph would get from the recipe as it is now — computed from the last
 * preview, so it follows every edit without a model call. The one deterministic
 * answer to "what happens when I press the button".
 */
/** Name comparison key — `subject_account`, `Subject Account` and `SubjectAccount` are the same. */
function nameKey(name: string | null | undefined): string {
  return (name ?? '').replace(/[^\p{L}\p{N}]+/gu, '').toLowerCase()
}

/**
 * The entity a relationship endpoint refers to — mirrors the backend resolver: exact name,
 * then the name ignoring case and punctuation, then the model's habit of writing
 * `subject` / `subject_account` for a subject entity it named by value ("Provazník Petr").
 */
export function resolveEndpoint(ref: string | null | undefined, recipe: Proposal): string | null {
  const subjects = recipe.subject_entities ?? []
  const names = [...subjects, ...(recipe.entity_descriptors ?? [])].map(e => e.name)
  if (ref && names.includes(ref)) return ref
  const wanted = nameKey(ref)
  const byKey = names.find(n => nameKey(n) === wanted)
  if (byKey) return byKey
  if (!wanted || wanted.startsWith('subject')) {
    const token = wanted.replace(/^subject/, '')
    const match = subjects.find(s => !token || nameKey(s.schema_type).includes(token))
    if (match) return match.name
    if (!token && subjects.length) return subjects[0].name
  }
  return null
}

export function summarizeOutput(recipe: Proposal, preview: Preview): OutputSummary {
  const nodes: OutputNode[] = [
    ...(recipe.subject_entities ?? []).map(s => ({
      name: s.name,
      label: (s.schema_type ?? '').replace(/^new:/, ''),
      source: 'subject' as const,
      count: s.identity_value ? 1 : 0,
      enabled: s.enabled ?? true,
      outsideSchema: s.schema_match === false,
    })),
    ...(recipe.entity_descriptors ?? []).map(d => ({
      name: d.name,
      label: (d.schema_type ?? '').replace(/^new:/, ''),
      source: 'row' as const,
      count: preview.descriptors.find(p => p.name === d.name)?.canonical_distinct ?? null,
      enabled: d.enabled ?? true,
      outsideSchema: d.schema_match === false,
    })),
  ]
  const edges: OutputEdge[] = (recipe.relationships ?? []).map(r => {
    const p = preview.relationships.find(x => x.name === r.name) ?? null
    const grain = effectiveGrain(r, p)
    return {
      name: r.name,
      type: bareType(r.type),
      kind: r.kind ?? 'event_aggregate',
      from: resolveEndpoint(r.from, recipe) ?? r.from,
      to: resolveEndpoint(r.to, recipe) ?? r.to,
      grain,
      count: p?.grains[grain] ?? null,
      rowsInScope: p?.rows_in_scope ?? null,
      measures: r.kind === 'structural' ? [] : (r.measures ?? []),
      enabled: r.enabled ?? true,
      outsideSchema: r.schema_match === false,
    }
  })
  const sum = (values: Array<number | null>) =>
    values.some(v => v === null) ? null : values.reduce<number>((a, b) => a + (b ?? 0), 0)
  const onNodes = nodes.filter(n => n.enabled)
  const onEdges = edges.filter(e => e.enabled)
  return {
    nodes,
    edges,
    totals: {
      nodes: sum(onNodes.map(n => n.count)),
      edges: sum(onEdges.map(e => e.count)),
      labels: new Set(onNodes.map(n => n.label)).size,
      types: new Set(onEdges.map(e => e.type)).size,
    },
    windows: !!recipe.windows?.enabled,
  }
}

type SchemaApprovalItem = SubjectEntity | EntityDescriptor | RelationshipRule

function needsSchemaApproval(item: SchemaApprovalItem): boolean {
  return (item.enabled ?? true) && item.schema_match === false
}

/** Enabled mappings that introduce entity, property or relationship schema changes. */
export function schemaApprovalItems(recipe: Proposal): SchemaApprovalItem[] {
  return [
    ...(recipe.subject_entities ?? []),
    ...(recipe.entity_descriptors ?? []),
    ...(recipe.relationships ?? []),
  ].filter(needsSchemaApproval)
}

export function withSchemaApproval(recipe: Proposal, approved: boolean): Proposal {
  const update = <T extends SchemaApprovalItem>(item: T): T =>
    needsSchemaApproval(item) ? { ...item, accept_new_type: approved } : item
  return {
    ...recipe,
    subject_entities: recipe.subject_entities?.map(update),
    entity_descriptors: recipe.entity_descriptors?.map(update),
    relationships: recipe.relationships?.map(update),
  }
}

/** Enabled items the projection would refuse until their new type is allowed. */
export function unacceptedNewTypes(recipe: Proposal): string[] {
  return schemaApprovalItems(recipe)
    .filter(item => !item.accept_new_type)
    .map(item => item.name)
}

export interface WindowsRule {
  enabled?: boolean
  text_columns?: string[]
  reason?: string
}

export interface ColumnAnnotation {
  name: string
  role: string
  extract_entities?: boolean
  /** Name of the entity descriptor or relationship receiving this column. */
  entity?: string | null
  /** Exact property from the entity or relationship's case schema receiving this column. */
  property?: string | null
  /** Property name for an unnamed or cryptic column (`col_3` → `tag`); facets are stored under it. */
  alias?: string | null
  normalizer?: string | null
  note?: string | null
}

export interface DerivedColumn {
  name: string
  source_column: string
  /** Text cells: the literal cell shape with {name} placeholders, and the placeholder read. */
  template?: string
  field?: string
  /** JSON object cells: the dotted key read, such as props.city. */
  path?: string
}

export interface DerivedColumnPreview {
  name: string
  source_column: string
  matched_rows: number
  examples: string[]
  /** Why the column could not be read from this table; it is left out of the mapping. */
  error?: string
}

export interface Proposal {
  shape?: string
  subject?: { source?: string; value?: string | null; description?: string }
  subject_entities?: SubjectEntity[]
  columns?: ColumnAnnotation[]
  /** Selected cells are extracted jointly per row, using that row's mapped entities as context. */
  row_text_columns?: string[]
  derived_columns?: DerivedColumn[]
  entity_descriptors?: EntityDescriptor[]
  relationships?: RelationshipRule[]
  event_patterns?: Array<{ template: string; event_type: string; regex: string }>
  windows?: WindowsRule
  /** Placeholders this file uses for "no value" (on top of the built-in N/A, -, XNA …). */
  null_tokens?: string[]
  warnings?: string[]
  plain_summary?: string
}

export interface DescriptorPreview {
  name: string
  schema_type: string
  identity_columns: string[]
  canonical_distinct: number
  raw_distinct: number
  rows_with_identity: number
  examples: string[]
}

export interface RelationshipPreview {
  name: string
  type: string
  from: string
  to: string
  rows_in_scope: number
  rows_total: number
  measure: string | null
  grains: Record<string, number>
  top: Array<{ from?: string; to: string; rows: number; sum: number | null }>
}

/** A value that does not fit its property's type (ADR-0081); `position` is the 0-based row. */
export interface InvalidCell {
  position: number
  column: string
  /** `Label.property` or `RELATIONSHIP_TYPE.property` */
  property: string
  value: string
  expected: 'number' | 'integer' | 'date' | 'boolean'
}

/** Rows a projection leaves out whole; `cells` lists the first of them, `cellCount` counts all. */
export interface LeftOutRows {
  rows: number
  cellCount: number
  cells: InvalidCell[]
}

/** A source column that reaches no entity and no link, and what becomes of it instead. */
export interface UnmappedColumn {
  name: string
  filled_rows: number
  reason: 'unused' | 'searchable' | 'read_by_ai' | 'ignored' | 'empty'
}

/** A link the schema allows between entities on the same rows that the mapping does not make. */
export interface MissingLink {
  relationship: RelationshipRule
  shared_rows: number
}

export interface Preview {
  extraction_cells?: number
  normalized_recipe?: Proposal
  validation_errors?: string[]
  derived_columns?: DerivedColumnPreview[]
  descriptors: DescriptorPreview[]
  relationships: RelationshipPreview[]
  /** What the projection could not build — blocks the write. */
  warnings: string[]
  /** Data the recipe would silently drop — advisory. */
  coverage?: string[]
  /** Links the mapping leaves out — advisory, the analyst decides. */
  missing_links?: MissingLink[]
  /** Source columns that reach no entity and no link, most worth a look first. */
  unmapped_columns?: UnmappedColumn[]
  /** Row text that would become searchable — counted even while windows are off. */
  windows?: {
    enabled: boolean
    count: number | null
    groups: number | null
    text_columns: string[]
  }
  /** Rows the projection would leave out (the preview payload stays snake_case). */
  left_out?: {
    rows: number
    cell_count: number
    cells: InvalidCell[]
    /** Validation error caused by entities losing every row to invalid mapped values. */
    blocking_error?: string | null
  }
}

/** The preview's left-out rows in the projection result's shape. */
export function previewLeftOut(preview: Preview): LeftOutRows | null {
  const leftOut = preview.left_out
  return leftOut
    ? { rows: leftOut.rows, cellCount: leftOut.cell_count, cells: leftOut.cells }
    : null
}

export interface LlmCall {
  model: string | null
  latency_s: number | null
  usage: { prompt_tokens?: number | null; completion_tokens?: number | null }
  error: string | null
  raw?: string | null
}

export interface RowWindowsResult {
  enabled: boolean
  count: number
  groups: number
  pruned: number
  text_columns: string[]
}

export interface ProjectionResult {
  projectionId: string
  run: string
  nodes: Record<string, number>
  edges: Record<string, number>
  warnings: string[]
  /** A (re-)projection is a full re-run: the previous footprint is deleted first. */
  prunedEdges: number
  deletedNodes?: number
  groupsPruned?: number
  windows?: RowWindowsResult | null
  confirmedSha256: string
  /** The run recorded in the pipelines history for this projection. */
  extractionRunId?: string | null
  /** Rows left out because a value does not fit its type; absent on older projections. */
  leftOut?: LeftOutRows | null
}

/** How the table's `structured_projection` job is doing (ADR-0006). */
export interface ProjectionRun {
  runId: string
  jobId?: string | null
  /** pending | running | done | failed */
  status: string
  error?: string | null
  startedAt?: string | null
  endedAt?: string | null
}

/** "Add to the case" queues a job; the analysis carries its `projectionRun` while it runs. */
export interface ProjectionQueued {
  status: 'queued'
  extractionRunId: string
  jobId: string
  confirmedSha256: string
}

export function isProjecting(table: Pick<StructuredIngestTable, 'projectionRun'>): boolean {
  const status = table.projectionRun?.status
  return status === 'pending' || status === 'running' || status === 'routing'
}

export interface StructuredIngestTable {
  tableId: string
  status: AnalysisStatus
  error?: string | null
  facts: TableFacts
  lane: LaneDecision
  columns: ColumnProfile[]
  measureColumns: string[]
  proposal: Proposal
  preview: Preview
  llm: LlmCall
  prompt: string
  instructions?: string | null
  conversation?: MappingMessage[]
  mappingRevision?: string | null
  reviewRecipe?: Proposal | null
  /** Object-storage key of the verbatim parquet once it is in the evidence catalogue. */
  parquetKey?: string | null
  /** The investigator's last confirmed recipe — what the screen starts from once a projection ran. */
  confirmed?: { sha256: string; at: string; recipe?: Proposal } | null
  projection?: ProjectionResult | null
  projectionRun?: ProjectionRun | null
}

export interface MappingMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  createdAt: string
}

/** The schema the mapping assistant was given — an analysis pins the one it was proposed with. */
export interface SchemaInfo {
  /** `case` = the case's own schema (or the globally active one); `seed` = the built-in fallback. */
  source: 'case' | 'seed'
  name?: string | null
  version?: string | number | null
  schemaId?: string | null
  labels: string[]
  /** Accepted schema flags, including non-unique properties available for manual edits. */
  propertyUniqueness?: Record<string, Record<string, boolean>>
  relationshipTypes: string[]
  /** Allowed `[from, TYPE, to]` triples. */
  patterns: string[][]
}

export interface StructuredIngestAnalysis {
  analysisId: string
  caseId: string
  filename: string
  createdAt: string
  status: AnalysisStatus
  error?: string | null
  documentId?: string | null
  instructions?: string | null
  caseContext?: string | null
  schemaLabels: string[]
  schemaInfo?: SchemaInfo | null
  tables: StructuredIngestTable[]
}

export interface StructuredIngestAnalysisSummary {
  analysisId: string
  filename: string
  createdAt: string
  status: AnalysisStatus
  documentId?: string | null
  tableCount: number
  /** At least one table was projected into the case graph. */
  projected: boolean
  /** Every table has a confirmed mapping. */
  confirmed?: boolean
  /** Router lane of the first table; `structured_forced` means the chunk path would fail it. */
  lane?: string | null
}

/** What the documents list shows next to a tabular file. */
export type MappingState = 'preparing' | 'review' | 'projected' | 'failed'

/** The newest analysis of one uploaded document, reduced to what a document row needs. */
export interface DocumentMapping {
  analysisId: string
  filename: string
  state: MappingState
  lane: string | null
  confirmed?: boolean
}

export function mappingState(
  summary: Pick<StructuredIngestAnalysisSummary, 'status' | 'projected'>
): MappingState {
  if (summary.status === 'pending') return 'preparing'
  if (summary.status === 'failed') return 'failed'
  return summary.projected ? 'projected' : 'review'
}

/** The full analysis's counterpart of `summary.projected`: at least one table is in the graph. */
export function analysisProjected(analysis: Pick<StructuredIngestAnalysis, 'tables'>): boolean {
  return analysis.tables.some(table => table.projection)
}

/** The case list's entry for an analysis that was just started, until the list is read again. */
export function analysisSummary(
  analysis: StructuredIngestAnalysis
): StructuredIngestAnalysisSummary {
  return {
    analysisId: analysis.analysisId,
    filename: analysis.filename,
    createdAt: analysis.createdAt,
    status: analysis.status,
    documentId: analysis.documentId,
    tableCount: analysis.tables.length,
    projected: false,
  }
}

/** Newest analysis per document id (the list arrives newest first). */
export function mappingsByDocument(
  summaries: StructuredIngestAnalysisSummary[]
): Record<string, DocumentMapping> {
  const out: Record<string, DocumentMapping> = {}
  for (const s of summaries) {
    if (!s.documentId || out[s.documentId]) continue
    out[s.documentId] = {
      analysisId: s.analysisId,
      filename: s.filename,
      state: mappingState(s),
      lane: s.lane ?? null,
      confirmed: s.confirmed ?? false,
    }
  }
  return out
}

/** The row lens: one row under the edited recipe (snake_case payload, like the preview). */
export interface RowExplanation {
  position: number
  total: number
  timestamp: string | null
  values: Record<string, string>
  /** This row's values that do not fit their type — the projection writes nothing from it. */
  left_out?: InvalidCell[]
  entities: Array<{
    name: string
    label: string
    source: 'subject' | 'row'
    key: string
    display: string
    cells: Record<string, string>
    missing: boolean
    /** False means the row matches this mapping, but projection deliberately leaves it out. */
    enabled?: boolean
  }>
  relationships: Array<{
    name: string
    type: string
    kind: 'structural' | 'event_aggregate'
    from: string
    to: string
    in_scope: boolean
    from_key?: string
    to_key?: string
    grain: Record<string, string>
    edge_rows: number
    measures: Record<string, number | null>
    /** False means the relationship is visible for review but will not be projected. */
    enabled?: boolean
  }>
}

export interface RowsQuery {
  relationship?: string | null
  fromKey?: string | null
  toKey?: string | null
  grainKey?: string | null
  positions?: number[] | null
  offset?: number
  limit?: number
}

export interface TableRow {
  position: number
  values: Record<string, string>
}

export interface RowsPage {
  table: string
  filename: string
  columns: string[]
  rows: TableRow[]
  total: number
  offset: number
  limit: number
}

/** An entity a promoted row excerpt hangs off, with the real EXCERPT_OF edge id. */
export interface RowExcerptAnchor {
  id: string
  edgeId: string
  labels: string[]
  name?: string | null
}

/** Rows promoted into the graph as one evidence node (ADR-0005). */
export interface RowExcerpt {
  id: string
  excerptId: string
  name: string
  structuredTable: string
  rowIndices: number[]
  rowCount: number
  tsFrom?: string | null
  tsTo?: string | null
  note: string
  createdBy?: string | null
  createdAt?: string | null
  anchors: RowExcerptAnchor[]
}

export interface EdgeRowsPage extends RowsPage {
  relationship: string
  grainKey: string
  fromDisplay?: string | null
  toDisplay?: string | null
}

export function isAnalysisBusy(analysis: StructuredIngestAnalysis | null | undefined): boolean {
  if (!analysis) return false
  return (
    analysis.status === 'pending' ||
    analysis.tables.some(t => t.status === 'pending' || isProjecting(t))
  )
}

export const structuredIngestApi = {
  readiness(caseId: string) {
    return apiClient.get<{ ready: boolean; schemaInfo: SchemaInfo | null }>(
      `/v1/cases/${caseId}/structured-ingest/readiness`
    )
  },
  analyzeDocument(caseId: string, documentId: string, instructions?: string) {
    return apiClient.post<StructuredIngestAnalysis>(
      `/v1/cases/${caseId}/structured-ingest/documents/${documentId}/analyze`,
      { instructions: instructions?.trim() || null }
    )
  },

  analyze(caseId: string, file: File, instructions?: string) {
    const form = new FormData()
    form.append('file', file)
    if (instructions?.trim()) form.append('instructions', instructions.trim())
    return apiClient.post<StructuredIngestAnalysis>(
      `/v1/cases/${caseId}/structured-ingest/analyze`,
      form
    )
  },

  list(caseId: string) {
    return apiClient.get<StructuredIngestAnalysisSummary[]>(`/v1/cases/${caseId}/structured-ingest`)
  },

  get(caseId: string, analysisId: string) {
    return apiClient.get<StructuredIngestAnalysis>(
      `/v1/cases/${caseId}/structured-ingest/${analysisId}`
    )
  },

  preview(caseId: string, analysisId: string, tableId: string, recipe: Proposal) {
    return apiClient.post<Preview>(
      `/v1/cases/${caseId}/structured-ingest/${analysisId}/tables/${tableId}/preview`,
      { recipe }
    )
  },

  explainRow(
    caseId: string,
    analysisId: string,
    tableId: string,
    recipe: Proposal,
    position: number
  ) {
    return apiClient.post<RowExplanation>(
      `/v1/cases/${caseId}/structured-ingest/${analysisId}/tables/${tableId}/explain-row`,
      { recipe, position }
    )
  },

  rows(caseId: string, analysisId: string, tableId: string, query: RowsQuery) {
    return apiClient.post<RowsPage>(
      `/v1/cases/${caseId}/structured-ingest/${analysisId}/tables/${tableId}/rows`,
      query
    )
  },

  promoteRows(
    caseId: string,
    analysisId: string,
    tableId: string,
    body: { positions: number[]; note?: string }
  ) {
    return apiClient.post<RowExcerpt>(
      `/v1/cases/${caseId}/structured-ingest/${analysisId}/tables/${tableId}/excerpts`,
      body
    )
  },

  listRowExcerpts(caseId: string, analysisId: string, tableId: string) {
    return apiClient.get<RowExcerpt[]>(
      `/v1/cases/${caseId}/structured-ingest/${analysisId}/tables/${tableId}/excerpts`
    )
  },

  edgeRows(caseId: string, relationshipId: string, offset = 0, limit = 100) {
    const params = new URLSearchParams({ offset: String(offset), limit: String(limit) })
    return apiClient.get<EdgeRowsPage>(
      `/v1/cases/${caseId}/structured-ingest/edge-rows/${encodeURIComponent(relationshipId)}?${params}`
    )
  },

  reannotate(
    caseId: string,
    analysisId: string,
    tableId: string,
    instructions: string,
    recipe?: Proposal
  ) {
    return apiClient.post<StructuredIngestAnalysis>(
      `/v1/cases/${caseId}/structured-ingest/${analysisId}/tables/${tableId}/reannotate`,
      { instructions, recipe }
    )
  },

  project(caseId: string, analysisId: string, tableId: string, recipe: Proposal) {
    return apiClient.post<ProjectionQueued>(
      `/v1/cases/${caseId}/structured-ingest/${analysisId}/tables/${tableId}/project`,
      { recipe }
    )
  },
}
